// ============================================================
// MongoDBCrudCreate.jsx
//
// A branded, interactive guide to creating data in MongoDB.
// Covers insertOne, insertMany, and Schema Flexibility.
//
// Brand palette:
//   Background  → #001E2B  (MongoDB Deep Navy)
//   Primary     → #00ED64  (MongoDB Spring Green)
//   Text        → #FFFFFF  (White)
//   Code        → #010409  (GitHub Dark)
// ============================================================

import React, { useState } from "react";
import {
    Box,
    Container,
    Typography,
    Grid,
    Chip,
    Divider,
    Paper,
    Stack,
    IconButton,
    Avatar,
    Alert,
} from "@mui/material";

/* MUI Icons */
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import DynamicFeedIcon from "@mui/icons-material/DynamicFeed";
import PlaylistAddIcon from "@mui/icons-material/PlaylistAdd";
import SchoolIcon from "@mui/icons-material/School";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DoneIcon from "@mui/icons-material/Done";
import TerminalIcon from "@mui/icons-material/Terminal";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";

/* ── Brand tokens ────────────────────────────────────────── */
const BRAND = {
    bg: "#001E2B",
    surface: "rgba(255,255,255,0.04)",
    border: "rgba(0, 237, 100, 0.2)",
    borderHover: "rgba(0, 237, 100, 0.6)",
    green: "#00ED64",
    white: "#FFFFFF",
    muted: "rgba(255,255,255,0.6)",
    codeBg: "#010409",
};

/* ── Sub-Components ──────────────────────────────────────── */

function CodeTerminal({ code, label, color = BRAND.green }) {
    const [copied, setCopied] = useState(false);
    const handleCopy = () => {
        navigator.clipboard.writeText(code);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    return (
        <Box sx={{ background: BRAND.codeBg, borderRadius: 3, border: `1px solid ${BRAND.border}`, overflow: "hidden", mb: 4 }}>
            <Box sx={{ px: 2, py: 1, display: "flex", justifyContent: "space-between", background: "rgba(255,255,255,0.02)" }}>
                <Typography variant="caption" sx={{ color: color, fontWeight: 700, fontFamily: "monospace" }}>{label}</Typography>
                <IconButton size="small" onClick={handleCopy} sx={{ color: BRAND.muted }}>
                    {copied ? <DoneIcon fontSize="small" /> : <ContentCopyIcon fontSize="small" />}
                </IconButton>
            </Box>
            <Box sx={{ p: 2.5, overflowX: "auto" }}>
                <Typography component="pre" sx={{ fontFamily: "'Fira Code', monospace", fontSize: 13, color: "#D1D5DB", lineHeight: 1.5 }}>
                    {code}
                </Typography>
            </Box>
        </Box>
    );
}

/* ── Main Component ──────────────────────────────────────── */

export default function MongoDBCrudCreate() {
    return (
        <Box sx={{ minHeight: "100vh", background: BRAND.bg, py: { xs: 6, md: 10 }, color: BRAND.white }}>
            <Container maxWidth="lg">

                {/* 1. HERO HEADER */}
                <Box sx={{ mb: 10, textAlign: { xs: "center", md: "left" } }}>
                    <Chip
                        icon={<SchoolIcon style={{ color: BRAND.green }} />}
                        label="CRUD Operations · Module 05"
                        sx={{ bgcolor: "rgba(0,237,100,0.1)", color: BRAND.green, border: `1px solid ${BRAND.border}`, mb: 2 }}
                    />
                    <Typography variant="h2" sx={{ fontWeight: 900, mb: 2, letterSpacing: "-1.5px" }}>
                        Create <span style={{ color: BRAND.green }}>Operations</span>
                    </Typography>
                    <Typography sx={{ color: BRAND.muted, fontSize: 18, maxWidth: 800, lineHeight: 1.7 }}>
                        In MongoDB, "Creating" means inserting BSON documents into collections.
                        Unlike SQL, you don't need to define columns first—just pass your object and MongoDB handles the rest.
                    </Typography>
                </Box>

                {/* 2. INSERT ONE */}
                <Grid container spacing={6} alignItems="center" sx={{ mb: 10 }}>
                    <Grid item xs={12} md={5}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}>
                            <AddCircleOutlineIcon sx={{ color: BRAND.green, fontSize: 32 }} />
                            <Typography variant="h5" sx={{ fontWeight: 800 }}>insertOne()</Typography>
                        </Box>
                        <Typography sx={{ color: BRAND.muted, mb: 3, lineHeight: 1.8 }}>
                            Use <code>insertOne()</code> to add a single document to a collection. If the collection
                            doesn't exist yet, MongoDB will create it automatically upon the first insertion.
                        </Typography>
                        <Alert severity="info" sx={{ bgcolor: "rgba(0,237,100,0.05)", color: BRAND.white, border: `1px solid ${BRAND.border}` }}>
                            <Typography variant="body2">
                                <strong>Automatic _id:</strong> If you don't provide an <code>_id</code> field,
                                MongoDB adds a unique <strong>ObjectId</strong> for you.
                            </Typography>
                        </Alert>
                    </Grid>
                    <Grid item xs={12} md={7}>
                        <CodeTerminal
                            label="mongosh"
                            code={`db.products.insertOne({\n  name: "Wireless Mouse",\n  price: 25.99,\n  stock: 100,\n  tags: ["electronics", "peripherals"]\n});`}
                        />
                    </Grid>
                </Grid>



                <Divider sx={{ borderColor: "rgba(255,255,255,0.05)", mb: 10 }} />

                {/* 3. INSERT MANY */}
                <Grid container spacing={6} alignItems="center" sx={{ mb: 10 }}>
                    <Grid item xs={12} md={7} order={{ xs: 2, md: 1 }}>
                        <CodeTerminal
                            label="mongosh"
                            code={`db.products.insertMany([\n  { name: "Keyboard", price: 45.00 },\n  { name: "Monitor", price: 199.99 },\n  { name: "HDMI Cable", price: 12.50 }\n]);`}
                        />
                    </Grid>
                    <Grid item xs={12} md={5} order={{ xs: 1, md: 2 }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}>
                            <PlaylistAddIcon sx={{ color: BRAND.green, fontSize: 32 }} />
                            <Typography variant="h5" sx={{ fontWeight: 800 }}>insertMany()</Typography>
                        </Box>
                        <Typography sx={{ color: BRAND.muted, mb: 3, lineHeight: 1.8 }}>
                            To add multiple documents at once, pass an <strong>array of objects</strong> to <code>insertMany()</code>.
                            This is significantly faster than calling <code>insertOne</code> multiple times because it reduces network round-trips.
                        </Typography>
                    </Grid>
                </Grid>

                {/* 4. SCHEMA FLEXIBILITY */}
                <Box sx={{ mb: 10, p: 4, borderRadius: 6, bgcolor: "rgba(0, 237, 100, 0.03)", border: `1px solid ${BRAND.border}` }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}>
                        <DynamicFeedIcon sx={{ color: BRAND.green }} />
                        <Typography variant="h5" sx={{ fontWeight: 800 }}>The Power of Flexibility</Typography>
                    </Box>
                    <Typography variant="body1" sx={{ color: BRAND.muted, mb: 4 }}>
                        Notice how these two documents can live in the <strong>same collection</strong> despite having different fields.
                        This is "Polymorphism" in action.
                    </Typography>

                    <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                            <CodeTerminal label="Standard Document" code={`{\n  "type": "book",\n  "title": "Clean Code",\n  "author": "Robert Martin"\n}`} />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <CodeTerminal label="Extended Document" code={`{\n  "type": "ebook",\n  "title": "JavaScript Patterns",\n  "fileSize": "5MB",\n  "format": "PDF"\n}`} />
                        </Grid>
                    </Grid>
                </Box>



                {/* 5. PRO-TIP FOR JUNIORS */}
                <Box sx={{ mb: 10 }}>
                    <Paper sx={{ p: 3, bgcolor: "rgba(255,255,255,0.02)", border: `1px solid ${BRAND.border}`, borderRadius: 4 }}>
                        <Stack direction="row" spacing={2}>
                            <Avatar sx={{ bgcolor: BRAND.green }}><TerminalIcon /></Avatar>
                            <Box>
                                <Typography variant="h6" sx={{ color: BRAND.white, fontWeight: 700 }}>Understanding the Response</Typography>
                                <Typography variant="body2" sx={{ color: BRAND.muted, mt: 1 }}>
                                    When you run an insert, MongoDB returns an <strong>Acknowledgment</strong> object.
                                    Always check <code>acknowledged: true</code> and take note of the <code>insertedId</code>—you'll often
                                    need it to link documents in your application logic.
                                </Typography>
                            </Box>
                        </Stack>
                    </Paper>
                </Box>

                {/* FOOTER CTA */}
                <Box sx={{
                    p: 8, borderRadius: 8, textAlign: "center",
                    background: `linear-gradient(45deg, ${BRAND.bg}, #00684A)`,
                    border: `1px solid ${BRAND.border}`,
                    boxShadow: `0 20px 40px rgba(0,0,0,0.5)`
                }}>
                    <AddCircleOutlineIcon style={{ fontSize: 40, color: BRAND.green, marginBottom: 16 }} />
                    <Typography variant="h5" sx={{ fontWeight: 800, mb: 1 }}>Data Injected?</Typography>
                    <Typography sx={{ color: BRAND.muted, mb: 4 }}>Now that you have data in your collection, let's learn how to find it using Read operations.</Typography>
                    <Stack direction="row" spacing={2} justifyContent="center">
                        <IconButton sx={{ bgcolor: BRAND.green, color: BRAND.bg, p: 2, borderRadius: 2 }}>
                            <Typography variant="button" sx={{ fontWeight: 900 }}>Go to Read Operations</Typography>
                        </IconButton>
                    </Stack>
                </Box>

            </Container>
        </Box>
    );
}
